type ShareCardPriorityLike = {
  label: string;
  emoji?: string | null;
  reason?: string | null;
};

type ShareCardSectionLike = {
  label: string;
  emoji?: string | null;
  flags?: string[] | null;
};

type ShareCardSummaryLike = {
  headline?: string | null;
  estimatedImpactMin?: number | null;
  estimatedImpactMax?: number | null;
  priorities?: ShareCardPriorityLike[] | null;
  sectionReports?: ShareCardSectionLike[] | null;
};

export type LifeCheckShareCardData = {
  impactLabel: string;
  headline: string;
  findings: string[];
};

function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function chunkText(value: string, maxLength: number) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxLength) {
      current = candidate;
      return;
    }

    if (current) {
      lines.push(current);
    }
    current = word;
  });

  if (current) {
    lines.push(current);
  }

  return lines;
}

function formatImpactLabel(summary: ShareCardSummaryLike | null | undefined) {
  const min = summary?.estimatedImpactMin ?? null;
  const max = summary?.estimatedImpactMax ?? null;

  if (typeof min === 'number' && typeof max === 'number' && min > 0 && max > 0) {
    return `£${min.toLocaleString('en-GB')} – £${max.toLocaleString('en-GB')} per year`;
  }

  if (typeof max === 'number' && max > 0) {
    return `Up to £${max.toLocaleString('en-GB')} per year`;
  }

  return 'A calmer picture of what matters next';
}

export function buildLifeCheckShareFindings(summary: ShareCardSummaryLike | null | undefined, maxItems = 3) {
  const findings: string[] = [];

  (summary?.priorities || []).forEach((priority) => {
    if (findings.length >= maxItems) return;
    const reason = priority.reason?.trim();
    if (!reason) return;
    findings.push(`${priority.emoji || ''} ${priority.label}: ${reason}`.trim());
  });

  if (findings.length < maxItems) {
    (summary?.sectionReports || []).forEach((section) => {
      if (findings.length >= maxItems) return;
      const flag = section.flags?.find((entry) => entry.trim().length > 0)?.trim();
      if (!flag) return;
      findings.push(`${section.emoji || ''} ${section.label}: ${flag}`.trim());
    });
  }

  return findings.slice(0, maxItems);
}

export function buildLifeCheckShareCardData(summary: ShareCardSummaryLike | null | undefined): LifeCheckShareCardData {
  return {
    impactLabel: formatImpactLabel(summary),
    headline: summary?.headline?.trim() || 'Adulting highlighted the parts of life worth checking next.',
    findings: buildLifeCheckShareFindings(summary, 3),
  };
}

export function buildLifeCheckShareCardSvg(summary: ShareCardSummaryLike | null | undefined) {
  const card = buildLifeCheckShareCardData(summary);
  const headlineLines = chunkText(card.headline, 42).slice(0, 3);
  const findingLines = card.findings.length > 0 ? card.findings : ['A quick check across money, home, health, people, and work.'];

  const findingMarkup = findingLines
    .slice(0, 3)
    .map((finding, index) => {
      const y = 382 + (index * 64);
      return `
      <rect x="72" y="${y - 26}" width="1056" height="50" rx="18" fill="rgba(255,255,255,0.12)" />
      <text x="102" y="${y}" fill="#FFFFFF" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="26" font-weight="600">${escapeXml(finding)}</text>`;
    })
    .join('');

  const headlineMarkup = headlineLines
    .map((line, index) => `<tspan x="72" dy="${index === 0 ? 0 : 40}">${escapeXml(line)}</tspan>`)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="1200" height="628" viewBox="0 0 1200 628" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Adulting Life Check result">
  <rect width="1200" height="628" rx="42" fill="#5C6B47"/>
  <circle cx="1090" cy="86" r="128" fill="rgba(255,255,255,0.08)"/>
  <circle cx="1024" cy="548" r="156" fill="rgba(200,146,42,0.18)"/>
  <rect x="72" y="56" width="66" height="66" rx="21" fill="#FFFFFF"/>
  <image href="/adulting-icon.png" x="88" y="72" width="34" height="30" preserveAspectRatio="xMidYMid meet"/>
  <text x="160" y="94" fill="#FFFFFF" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="30" font-weight="700">Adulting Life Check</text>
  <text x="72" y="188" fill="#C8922A" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="54" font-weight="800">${escapeXml(card.impactLabel)}</text>
  <text x="72" y="248" fill="rgba(255,255,255,0.92)" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="30" font-weight="500">${headlineMarkup}</text>
  <text x="72" y="334" fill="rgba(255,255,255,0.58)" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="20" font-weight="600" letter-spacing="0.12em">HEADLINE FINDINGS</text>
  ${findingMarkup}
  <text x="72" y="584" fill="rgba(255,255,255,0.7)" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="20" font-weight="500">adultingapp.uk</text>
</svg>`;
}
